import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import { MapPin, Car, Plane, CheckCircle, Navigation } from 'lucide-react';
import { accessibilityData } from '../../data/mock';
import L from 'leaflet';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const songonIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background: #2D5A27; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

const locations = [
  { name: "Les Terres de Songon", coords: [5.3364, -4.2667], main: true },
  { name: "Le Plateau (Abidjan)", coords: [5.3200, -4.0167], time: "~30 min" },
  { name: "Yopougon Gesco", coords: [5.3500, -4.1000], time: "~12 min" },
  { name: "Yopougon Siporex", coords: [5.3333, -4.0833], time: "~25 min" },
  { name: "Carrefour Jacqueville", coords: [5.2167, -4.4167], time: "~5 min" },
  { name: "Aéroport FHB", coords: [5.2619, -3.9262], time: "~50 min" },
];

const getRouteLine = (fromCoords) => [fromCoords, locations[0].coords];

const RouteCard = ({ route, index, isHighlighted, onMouseEnter, onMouseLeave, onClick }) => (
  <div 
    className={`p-4 rounded-xl cursor-pointer transition-all duration-300 ${
      isHighlighted
        ? 'bg-[#2D5A27] text-white shadow-lg scale-[1.02]' 
        : 'bg-white hover:bg-[#F5F5F5] border border-gray-100'
    }`}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    onClick={onClick}
    data-testid={`route-card-${index}`}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          isHighlighted ? 'bg-white/20' : 'bg-[#F5F5F5]'
        }`}>
          {route.from.includes('Aéroport') ? (
            <Plane className={`w-5 h-5 ${isHighlighted ? 'text-white' : 'text-[#666]'}`} />
          ) : (
            <Car className={`w-5 h-5 ${isHighlighted ? 'text-white' : 'text-[#666]'}`} />
          )}
        </div>
        <div>
          <p className={`font-medium text-sm ${isHighlighted ? 'text-white' : 'text-[#1a1a1a]'}`}>
            {route.from}
          </p>
          <p className={`text-xs ${isHighlighted ? 'text-white/70' : 'text-[#999]'}`}>
            {route.via}
          </p>
        </div>
      </div>
      <div className={`text-2xl font-light ${isHighlighted ? 'text-white' : 'text-[#2D5A27]'}`} 
           style={{ fontFamily: 'Playfair Display, serif' }}>
        {route.duration}
      </div>
    </div>
  </div>
);

const InfrastructureSection = () => (
  <div className="bg-[#1a1a1a] rounded-2xl p-8 lg:p-12">
    <div className="flex flex-col lg:flex-row gap-12 items-center">
      <div className="lg:w-1/3">
        <div className="flex items-center gap-3 mb-4">
          <Navigation className="w-6 h-6 text-[#2D5A27]" />
          <h3 className="text-xl text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
            Nouvelles infrastructures
          </h3>
        </div>
        <p className="text-white/60 text-sm">
          Les récents investissements routiers ont transformé Songon en hub résidentiel stratégique.
        </p>
      </div>
      <div className="lg:w-2/3 grid sm:grid-cols-2 gap-4">
        {accessibilityData.infrastructures.map((infra) => (
          <div key={infra} className="flex items-center gap-3 bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-colors">
            <CheckCircle className="w-5 h-5 text-[#2D5A27] flex-shrink-0" />
            <span className="text-white/90 text-sm">{infra}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Accessibility = () => {
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <section className="py-24 lg:py-32 bg-gradient-to-b from-[#FAFAFA] to-white" data-testid="accessibility-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-[#2D5A27]/10 px-4 py-2 rounded-full mb-6">
            <MapPin className="w-4 h-4 text-[#2D5A27]" />
            <span className="text-[#2D5A27] text-sm font-medium tracking-wide">Accessibilité</span>
          </div>
          <h2 
            className="text-4xl lg:text-5xl text-[#1a1a1a] mb-6 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {accessibilityData.title}
          </h2>
          <p className="text-[#666] text-lg leading-relaxed">
            {accessibilityData.description}
          </p>
        </div>

        {/* Map and Routes Grid */}
        <div className="grid lg:grid-cols-5 gap-8 mb-16">
          {/* Interactive Map */}
          <div className="lg:col-span-3 bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100">
            <div className="h-[450px] relative">
              <MapContainer 
                center={[5.3364, -4.2667]} 
                zoom={10} 
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {locations.slice(1).map((loc, index) => (
                  <Polyline
                    key={`route-${loc.name}`}
                    positions={getRouteLine(loc.coords)}
                    pathOptions={{
                      color: hoveredCard === index || selectedRoute === index ? '#2D5A27' : '#999',
                      weight: hoveredCard === index || selectedRoute === index ? 4 : 2,
                      opacity: hoveredCard === index || selectedRoute === index ? 1 : 0.5,
                      dashArray: hoveredCard === index || selectedRoute === index ? null : '5, 10',
                    }}
                  />
                ))}

                {locations.map((loc) => (
                  <Marker 
                    key={`marker-${loc.name}`}
                    position={loc.coords}
                    icon={loc.main ? songonIcon : new L.Icon.Default()}
                  >
                    <Popup>
                      <div className="text-center p-2">
                        <strong className="text-[#1a1a1a]">{loc.name}</strong>
                        {loc.time && (
                          <p className="text-[#2D5A27] font-medium mt-1">{loc.time}</p>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
              
              {/* Map Legend */}
              <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg z-[1000]">
                <div className="flex items-center gap-2 text-xs text-[#666]">
                  <div className="w-3 h-3 bg-[#2D5A27] rounded-full"></div>
                  <span>Les Terres de Songon</span>
                </div>
              </div>
            </div>
          </div>

          {/* Routes List */}
          <div className="lg:col-span-2 space-y-3">
            <h3 className="text-lg font-medium text-[#1a1a1a] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Temps de trajet
            </h3>
            {accessibilityData.routes.map((route, index) => (
              <RouteCard
                key={route.from}
                route={route}
                index={index}
                isHighlighted={hoveredCard === index || selectedRoute === index}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => setSelectedRoute(selectedRoute === index ? null : index)}
              />
            ))}
          </div>
        </div>

        {/* Infrastructures */}
        <InfrastructureSection />
      </div>
    </section>
  );
};

export default Accessibility;
